import { firestore, doc, getDoc, updateDoc, setDoc, increment, serverTimestamp, collection, query, where, getDocs, orderBy, limit } from "@/firebase/init";

class ReportTrackingService {
  /**
   * Track a report view - updates both global stats and user history
   * @param {string} reportTo - The route name of the report (e.g., "sales-orders-report")
   * @param {string} userId - The user ID viewing the report
   */
  static async trackReportView(reportTo, userId) {
    try {
      if (!reportTo || !userId) return;

      // Update global report view count
      const reportStatsRef = doc(firestore, "reportStats", reportTo);
      const reportStatsSnap = await getDoc(reportStatsRef);
      
      if (reportStatsSnap.exists()) {
        await updateDoc(reportStatsRef, {
          viewCount: increment(1),
          lastViewed: serverTimestamp(),
        });
      } else {
        await setDoc(reportStatsRef, {
          reportTo,
          viewCount: 1,
          lastViewed: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }

      // Update user's report view history
      const userRef = doc(firestore, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const reportViews = userData.reportViews || [];
        
        // Remove existing entry if present and add to front (most recent first)
        const filteredViews = reportViews.filter(v => v.reportTo !== reportTo);
        const updatedViews = [
          { reportTo, viewedAt: new Date() },
          ...filteredViews
        ].slice(0, 20); // Keep only last 20 viewed reports

        await updateDoc(userRef, {
          reportViews: updatedViews,
        });
      }
    } catch (error) {
      console.error("Error tracking report view:", error);
    }
  }

  /**
   * Get global view counts for reports
   * @param {Array<string>} reportTos - Array of report route names
   * @returns {Object} Map of reportTo -> viewCount
   */
  static async getReportViewCounts(reportTos) {
    try {
      const counts = {};
      const promises = reportTos.map(async (reportTo) => {
        const reportStatsRef = doc(firestore, "reportStats", reportTo);
        const reportStatsSnap = await getDoc(reportStatsRef);
        counts[reportTo] = reportStatsSnap.exists() 
          ? (reportStatsSnap.data().viewCount || 0)
          : 0;
      });
      await Promise.all(promises);
      return counts;
    } catch (error) {
      console.error("Error getting report view counts:", error);
      return {};
    }
  }

  /**
   * Get user's recently viewed reports
   * @param {string} userId - The user ID
   * @param {number} limitCount - Maximum number of reports to return
   * @returns {Array} Array of reportTo strings
   */
  static async getRecentlyViewedReports(userId, limitCount = 5) {
    try {
      if (!userId) return [];
      
      const userRef = doc(firestore, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const reportViews = userSnap.data().reportViews || [];
        return reportViews
          .slice(0, limitCount)
          .map(v => v.reportTo);
      }
      return [];
    } catch (error) {
      console.error("Error getting recently viewed reports:", error);
      return [];
    }
  }

  /**
   * Get most popular reports globally
   * @param {number} limitCount - Maximum number of reports to return
   * @returns {Array} Array of { reportTo, viewCount } objects
   */
  static async getMostPopularReports(limitCount = 10) {
    try {
      const statsRef = collection(firestore, "reportStats");
      const q = query(statsRef, orderBy("viewCount", "desc"), limit(limitCount));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        reportTo: doc.id,
        viewCount: doc.data().viewCount || 0,
      }));
    } catch (error) {
      console.error("Error getting most popular reports:", error);
      return [];
    }
  }

  /**
   * Toggle favorite status for a report
   * @param {string} reportTo - The route name of the report
   * @param {string} userId - The user ID
   * @param {boolean} isFavorite - Whether to add or remove from favorites
   */
  static async toggleFavorite(reportTo, userId, isFavorite) {
    try {
      if (!reportTo || !userId) return;

      const userRef = doc(firestore, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const favoriteReports = userData.favoriteReports || [];
        
        let updatedFavorites;
        if (isFavorite) {
          // Add to favorites if not already present
          if (!favoriteReports.includes(reportTo)) {
            updatedFavorites = [...favoriteReports, reportTo];
          } else {
            updatedFavorites = favoriteReports;
          }
        } else {
          // Remove from favorites
          updatedFavorites = favoriteReports.filter(r => r !== reportTo);
        }

        await updateDoc(userRef, {
          favoriteReports: updatedFavorites,
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      throw error;
    }
  }

  /**
   * Get user's favorite reports
   * @param {string} userId - The user ID
   * @returns {Array} Array of reportTo strings
   */
  static async getFavoriteReports(userId) {
    try {
      if (!userId) return [];
      
      const userRef = doc(firestore, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data().favoriteReports || [];
      }
      return [];
    } catch (error) {
      console.error("Error getting favorite reports:", error);
      return [];
    }
  }
}

export default ReportTrackingService;

